import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  MessageCircle, 
  Send, 
  User,
  Loader2,
  Inbox,
  CheckCircle2,
  Clock,
  UserPlus,
  Filter,
  RefreshCw
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import DashboardLayout from "../../components/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  open: { label: "Abierta", color: "bg-blue-100 text-blue-700", icon: Clock },
  assigned: { label: "Asignada", color: "bg-purple-100 text-purple-700", icon: UserPlus },
  resolved: { label: "Resuelta", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  closed: { label: "Cerrada", color: "bg-navy-100 text-navy-600", icon: CheckCircle2 }
};

const relatedToLabels = {
  general: "General",
  case: "Caso",
  foia: "FOIA",
  documents: "Documentos",
  interview: "Entrevista",
  billing: "Facturación"
};

const AdminChat = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [oficiales, setOficiales] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    fetchOficiales();
  }, [filterStatus]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchOficiales = async () => {
    try {
      const res = await axios.get(`${API}/admin/staff/oficiales`);
      setOficiales(res.data || []);
    } catch (error) {
      console.error("Error fetching oficiales:", error);
    }
  };

  const fetchConversations = async () => {
    try {
      const params = filterStatus !== "all" ? `?status=${filterStatus}` : "";
      const res = await axios.get(`${API}/admin/conversations${params}`);
      setConversations(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${API}/conversations/${conversationId}`);
      setSelectedConversation(res.data.conversation);
      setMessages(res.data.messages);
    } catch (error) {
      toast.error("Error al cargar mensajes");
    } finally {
      setLoadingMessages(false);
    }
  };

  const selectConversation = (conv) => {
    fetchMessages(conv.id);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const res = await axios.post(
        `${API}/conversations/${selectedConversation.id}/messages`,
        { content: newMessage }
      );
      setMessages([...messages, res.data]);
      setNewMessage("");
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  const assignToMe = async () => {
    if (!selectedConversation) return;
    
    try {
      await axios.post(`${API}/admin/conversations/${selectedConversation.id}/assign`);
      toast.success("Conversación asignada a ti");
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      toast.error("Error al asignar conversación");
    }
  };

  const assignToOficial = async (oficialId) => {
    if (!selectedConversation || !oficialId) return;
    
    try {
      await axios.post(`${API}/admin/conversations/${selectedConversation.id}/assign?assign_to_id=${oficialId}`);
      const oficial = oficiales.find(o => o.id === oficialId);
      toast.success(`Conversación asignada a ${oficial?.name || 'oficial'}`);
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      toast.error("Error al asignar conversación");
    }
  };

  const updateStatus = async (status) => {
    if (!selectedConversation) return;
    
    try {
      await axios.post(`${API}/admin/conversations/${selectedConversation.id}/status?status=${status}`);
      toast.success("Estado actualizado");
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Ayer';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('es-ES', { weekday: 'short' });
    }
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const unreadCount = conversations.filter(c => c.unread_by_admin).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
                Centro de Mensajes
              </h1>
              <p className="text-navy-600">
                {unreadCount > 0 ? `${unreadCount} conversaciones sin leer` : "Todas las conversaciones al día"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="open">Abiertas</SelectItem>
                  <SelectItem value="assigned">Asignadas</SelectItem>
                  <SelectItem value="resolved">Resueltas</SelectItem>
                  <SelectItem value="closed">Cerradas</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchConversations}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card-elevated p-4">
            <p className="text-sm text-navy-500">Total</p>
            <p className="text-2xl font-bold text-navy-900">{conversations.length}</p>
          </div>
          <div className="card-elevated p-4">
            <p className="text-sm text-blue-600">Abiertas</p>
            <p className="text-2xl font-bold text-blue-600">
              {conversations.filter(c => c.status === "open").length}
            </p>
          </div>
          <div className="card-elevated p-4">
            <p className="text-sm text-purple-600">Asignadas</p>
            <p className="text-2xl font-bold text-purple-600">
              {conversations.filter(c => c.status === "assigned").length}
            </p>
          </div>
          <div className="card-elevated p-4">
            <p className="text-sm text-emerald-600">Resueltas</p>
            <p className="text-2xl font-bold text-emerald-600">
              {conversations.filter(c => c.status === "resolved" || c.status === "closed").length}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-350px)] min-h-[450px]">
          {/* Conversations List */}
          <div className="card-elevated overflow-hidden flex flex-col">
            <div className="p-4 border-b border-navy-100">
              <h2 className="font-semibold text-navy-900">Conversaciones</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <Inbox className="w-12 h-12 text-navy-300 mx-auto mb-3" />
                  <p className="text-navy-500 text-sm">No hay conversaciones</p>
                </div>
              ) : (
                <div className="divide-y divide-navy-100">
                  {conversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      className={`p-4 cursor-pointer transition-colors hover:bg-navy-50 ${
                        selectedConversation?.id === conv.id ? "bg-gold-50 border-l-4 border-gold-500" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1">
                          <h3 className={`font-medium text-sm truncate ${
                            conv.unread_by_admin ? "text-navy-900" : "text-navy-700"
                          }`}>
                            {conv.subject}
                          </h3>
                          <p className="text-xs text-navy-500">
                            {conv.user_name}
                          </p>
                        </div>
                        {conv.unread_by_admin && (
                          <span className="w-2 h-2 bg-gold-500 rounded-full flex-shrink-0 mt-1 ml-2"></span>
                        )}
                      </div>
                      <p className="text-xs text-navy-500 truncate mb-2">
                        {conv.last_message?.content || "Sin mensajes"}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig[conv.status]?.color}`}>
                            {statusConfig[conv.status]?.label}
                          </span>
                          <span className="text-xs text-navy-400 bg-navy-100 px-1.5 py-0.5 rounded">
                            {relatedToLabels[conv.related_to] || conv.related_to}
                          </span>
                        </div>
                        <span className="text-xs text-navy-400">
                          {formatTime(conv.last_message_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="lg:col-span-2 card-elevated overflow-hidden flex flex-col">
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-navy-100 bg-white">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="font-semibold text-navy-900">{selectedConversation.subject}</h2>
                      <p className="text-sm text-navy-600">
                        {selectedConversation.user_name} • {selectedConversation.user_email}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select 
                        value={selectedConversation.status} 
                        onValueChange={updateStatus}
                      >
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Abierta</SelectItem>
                          <SelectItem value="assigned">Asignada</SelectItem>
                          <SelectItem value="resolved">Resuelta</SelectItem>
                          <SelectItem value="closed">Cerrada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Assignment Section */}
                  <div className="mt-3 pt-3 border-t border-navy-100">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-navy-600">Asignar a:</span>
                      <Select 
                        value={selectedConversation.assigned_to || ""} 
                        onValueChange={(value) => assignToOficial(value)}
                      >
                        <SelectTrigger className="w-48 h-8">
                          <SelectValue placeholder="Seleccionar oficial" />
                        </SelectTrigger>
                        <SelectContent>
                          {oficiales.map((oficial) => (
                            <SelectItem key={oficial.id} value={oficial.id}>
                              {oficial.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedConversation.assigned_name && (
                        <span className="text-xs text-purple-600 flex items-center gap-1 bg-purple-50 px-2 py-1 rounded">
                          <User className="w-3 h-3" />
                          Asignado a: {selectedConversation.assigned_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-navy-50/50">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-8 h-8 animate-spin text-gold-600" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="w-12 h-12 text-navy-300 mx-auto mb-3" />
                      <p className="text-navy-500">Sin mensajes aún</p>
                    </div>
                  ) : (
                    messages.map((msg) => {
                      const isAdmin = msg.sender_role === "admin" || msg.sender_role === "asesor" || msg.sender_role === "legal";
                      return (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}
                        >
                          <div className={`max-w-[75%]`}>
                            <p className={`text-xs mb-1 ${isAdmin ? "text-right text-navy-500" : "text-navy-500"}`}>
                              {msg.sender_name}
                            </p>
                            <div className={`p-3 rounded-2xl ${
                              isAdmin 
                                ? "bg-purple-600 text-white rounded-br-md" 
                                : "bg-white text-navy-800 rounded-bl-md shadow-sm"
                            }`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                            <p className={`text-xs mt-1 ${isAdmin ? "text-right" : ""} text-navy-400`}>
                              {formatTime(msg.sent_at)}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Admin View Only Notice */}
                <div className="p-4 border-t border-navy-100 bg-amber-50">
                  <div className="flex items-center gap-3 text-amber-800">
                    <Clock className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">
                      <strong>Modo lectura:</strong> Como administrador, solo puedes ver y asignar conversaciones. 
                      El oficial asignado será quien responda al cliente.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-8">
                <div>
                  <MessageCircle className="w-16 h-16 text-navy-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-navy-700 mb-2">
                    Selecciona una conversación
                  </h3>
                  <p className="text-navy-500 text-sm">
                    Elige una conversación de la lista para ver los mensajes
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminChat;
